/* eslint-disable no-param-reassign */
import React, { useState, useCallback, useEffect } from "react"
import { Script } from "gatsby"

import { applyVideoCloudinaryTransforms } from "utils/applyCloudinaryTransforms"
import { getViewport } from "utils"

import { Container, JWPlayer, Title, Description } from "./styles"

/* eslint-disable no-plusplus */
let id = 1

/**
 * A video component that is either full-width or centered with three sizes
 * (small - 25%, medium - 50% and large - 75% width). When centered on mobile,
 * the width is always 100% with side padding.
 */
const Video = ({
  elRef,
  videoId,
  playerId,
  cloudinaryUrl,
  thumbnail,
  aspectRatio = 9 / 16,
  loop,
  mute,
  unmuteMobileAutoplay = false,
  noControls,
  autoPlay,
  floating,
  onPercentViewed = () => {},
  videoSignature,
  title,
  description,
  isGrid,
  destinationUrl,
  dataId,
  fullHeight,
  isVideoCard,
  hideOnPageLoad,
  fillContainer,
  onTimeCallBack,
  ...props
}) => {
  const [instanceId] = useState(`mbg-jw-player-${id++}`)
  const isMobile = getViewport() === "mobile"

  const playHandler = useCallback(() => {
    if (!isMobile || autoPlay) {
      return
    }

    const player = window.jwplayer(instanceId)
    player.setFullscreen(true)
    player.getContainer().querySelector("video").removeAttribute("playsinline")
  }, [instanceId, isMobile, autoPlay])

  const exitFullScreenHandler = useCallback(() => {
    if (!isMobile || autoPlay) {
      return
    }

    const player = window.jwplayer(instanceId)
    player.pause()
    player.setFloating(false)
    player.getContainer().querySelector("video").setAttribute("playsinline", true)
  }, [instanceId, isMobile, autoPlay])

  let cloudinaryThumb = null
  let tempThumb = null
  if (cloudinaryUrl) {
    const [left, right] = cloudinaryUrl.split("/video/upload/")
    cloudinaryThumb = `${left}/video/upload/so_0,f_auto,fl_lossy,q_auto/${right}`.replace(
      ".mp4",
      ".jpg",
    )
  } else {
    tempThumb = `https://content.jwplatform.com/v2/media/${videoId}/poster.jpg?width=720`
  }
  const playerScript = playerId || process.env.GATSBY_JW_PLAYER_SCRIPT

  useEffect(() => {
    if (videoId?.match(/placement/)) {
      const script = document.createElement("script")
      script.src = `https://cdn.jwplayer.com/v2/sites/ZFRapUZW/placements/${
        videoId.split(":")[1]
      }/embed.js`
      script.async = true
      document.getElementById("jw-placement-target").appendChild(script)
    }
  }, [videoId])

  const JWPlayerComponent = videoId?.match(/placement/) ? (
    <div id="jw-placement-target" />
  ) : (
    <JWPlayer
      fillContainer={fillContainer}
      fullHeight={fullHeight}
      isGrid={isGrid}
      playerId={instanceId}
      playerScript={playerScript}
      file={
        videoSignature ||
        (cloudinaryUrl && applyVideoCloudinaryTransforms(cloudinaryUrl, "w_1400,q_auto,f_auto"))
      }
      aspect={aspectRatio}
      onPlay={playHandler}
      onResume={playHandler}
      onExitFullScreen={exitFullScreenHandler}
      image={thumbnail || cloudinaryThumb || tempThumb}
      customProps={{
        autostart: autoPlay ? "viewable" : false,
        autoPause: { viewability: autoPlay },
        floating: { mode: floating ? "notVisible" : "never", dismissible: true },
        repeat: loop,
        mute: mute || (isMobile && autoPlay && !unmuteMobileAutoplay),
      }}
      noControls={noControls}
      onTwentyFivePercent={() => onPercentViewed(25)}
      onFiftyPercent={() => onPercentViewed(50)}
      onSeventyFivePercent={() => onPercentViewed(75)}
      onOneHundredPercent={() => onPercentViewed(100)}
      onTime={(event) => onTimeCallBack && onTimeCallBack(event)}
    />
  )

  return (
    <Container
      data-exp-id={dataId}
      ref={elRef}
      isGrid={isGrid}
      fullHeight={fullHeight}
      isVideoCard={isVideoCard}
      hideOnPageLoad={hideOnPageLoad}
      fillContainer={fillContainer}
      {...props}
    >
      {title?.childMarkdownRemark?.html && (
        <>
          <Title title={title.childMarkdownRemark.html.replace(/<\/?[^>]+(>|$)/g, "")} />
          {description?.childMarkdownRemark?.html && (
            <Description text={description.childMarkdownRemark.html} />
          )}
        </>
      )}
      {noControls && destinationUrl ? (
        <a href={destinationUrl} target="_blank" rel="noreferrer">
          {JWPlayerComponent}
        </a>
      ) : (
        JWPlayerComponent
      )}
    </Container>
  )
}

export default Video
